import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddAdminUnitDialog from './AddAdminUnitDialog';
import EditAdminUnitDialog from './EditAdminUnitDialog';
import api from '../utils/api';
import './Administrations.css';

const Administrations = () => {
  const navigate = useNavigate();
  const [adminUnits, setAdminUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedUnit, setDraggedUnit] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [dragOverUnit, setDragOverUnit] = useState(null);

  useEffect(() => {
    fetchAdminUnits();
  }, []);

  const fetchAdminUnits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin-units');
      
      if (response.data.success) {
        // Sort by index
        const sortedUnits = response.data.data.sort((a, b) => {
          if (a.index !== undefined && b.index !== undefined) {
            return a.index - b.index;
          }
          return 0;
        });
        setAdminUnits(sortedUnits);
      } else {
        throw new Error(response.data.message || 'Failed to fetch admin units');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch admin units');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUnit = async (unitData) => {
    try {
      const response = await api.post('/admin-units', unitData);
      
      if (response.data.success) {
        setAdminUnits([...adminUnits, response.data.data]);
        setShowAddDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to create admin unit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create admin unit');
    }
  };

  const handleEditUnit = async (id, unitData) => {
    try {
      const response = await api.put(`/admin-units/${id}`, unitData);
      
      if (response.data.success) {
        setAdminUnits(adminUnits.map(unit => 
          unit._id === id ? { ...unit, ...response.data.data } : unit
        ));
        setShowEditDialog(false);
        setSelectedUnit(null);
      } else {
        throw new Error(response.data.message || 'Failed to update admin unit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update admin unit');
    }
  };

  const handleDeleteUnit = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin unit?')) {
      return;
    }

    try {
      const response = await api.delete(`/admin-units/${id}`);
      
      if (response.data.success) {
        setAdminUnits(adminUnits.filter(unit => unit._id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete admin unit');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete admin unit');
    }
  };

  const openEditDialog = (unit) => {
    setSelectedUnit(unit);
    setShowEditDialog(true);
  };

  const openSubunits = (unit) => {
    navigate(`/administrations/${unit._id}/subunits`, { 
      state: { adminUnit: unit } 
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, unit) => {
    setDraggedUnit(unit);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, unit) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedUnit && draggedUnit._id !== unit._id) {
      setDragOverUnit(unit._id);
    }
  };

  const handleDrop = (e, targetUnit) => {
    e.preventDefault();
    
    if (!draggedUnit || draggedUnit._id === targetUnit._id) {
      return;
    }

    const draggedIndex = adminUnits.findIndex(u => u._id === draggedUnit._id);
    const targetIndex = adminUnits.findIndex(u => u._id === targetUnit._id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new array with reordered units
    const newUnits = [...adminUnits];
    const [removed] = newUnits.splice(draggedIndex, 1);
    newUnits.splice(targetIndex, 0, removed);

    // Update indices based on new order
    const updatedUnits = newUnits.map((unit, index) => ({
      ...unit,
      index: index
    }));

    setAdminUnits(updatedUnits);
    setDraggedUnit(null);
    setDragOverUnit(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedUnit(null);
    setDragOverUnit(null);
  };

  // Send updated indices to backend
  const sendUpdatedIndices = React.useCallback(async () => {
    if (!isReordering) {
      return;
    }

    try {
      const indices = adminUnits.map(unit => ({
        _id: unit._id,
        index: unit.index
      }));

      const response = await api.put('/admin-units/reorder', {
        units: indices
      });

      if (response.data.success) {
        setIsReordering(false);
      } else {
        // Revert to original order if update fails
        fetchAdminUnits();
      }
    } catch (err) {
      console.error('Error updating admin unit order:', err);
      // Revert to original order if update fails
      fetchAdminUnits();
    } finally {
      setIsReordering(false);
    }
  }, [adminUnits, isReordering]);

  // Auto-save indices after reordering
  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedIndices();
      }, 1000); // Wait 1 second after last reorder

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, sendUpdatedIndices]);

  const filteredUnits = adminUnits.filter(unit =>
    unit.name.toLowerCase().includes(searchTerm.toLowerCase())
  );



  if (loading) {
    return (
      <div className="administrations-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="administrations-container">
      <div className="administrations-header">
        <h2>Administrative Units</h2>
        <button 
          className="add-button"
          onClick={() => setShowAddDialog(true)}
        >
          + Add New Unit
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search admin units..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="admin-units-grid">
        {filteredUnits.length === 0 ? (
          <div className="no-units">
            {searchTerm ? 'No admin units found matching your search.' : 'No administrative units found.'}
          </div>
        ) : (
          filteredUnits.map((unit) => (
            <div 
              key={unit._id} 
              className={`admin-unit-card ${draggedUnit?._id === unit._id ? 'dragging' : ''} ${dragOverUnit === unit._id ? 'drag-over' : ''}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, unit)}
              onDragOver={(e) => handleDragOver(e, unit)}
              onDrop={(e) => handleDrop(e, unit)}
              onDragEnd={handleDragEnd}
            >
              <div className="unit-header">
                <div className="unit-name-container">
                  <h3 className="unit-name">{unit.name}</h3>
                  <div className="drag-icon-container">
                    <span className="drag-icon">⋮⋮</span>
                  </div>
                </div>
                <div className="unit-actions">
                  <div className="action-icons">
                    <button
                      onClick={() => openEditDialog(unit)}
                      className="edit-button"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteUnit(unit._id)}
                      className="delete-button"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                  <button
                    onClick={() => openSubunits(unit)}
                    className="view-button"
                    title="View Subunits"
                  >
                    View Subunits
                  </button>
                </div>
              </div>

            </div>
          ))
        )}
      </div>

      {showAddDialog && (
        <AddAdminUnitDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddUnit}
        />
      )}

      {showEditDialog && selectedUnit && (
        <EditAdminUnitDialog
          unit={selectedUnit}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedUnit(null);
          }}
          onEdit={handleEditUnit}
        />
      )}

    </div>
  );
};

export default Administrations;
